import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dumbbell, Target, AlertTriangle, CheckCircle, RefreshCw,
  ChevronRight, Settings, Zap, Shield, Clock, User,
  Home, Building, Activity, Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT_PRESETS = {
  full_gym: { name: "Full Gym", icon: Building, description: "Complete gym facility" },
  home_gym: { name: "Home Gym", icon: Home, description: "Basic home setup" },
  bodyweight_only: { name: "Bodyweight Only", icon: User, description: "No equipment needed" },
  resistance_bands: { name: "Resistance Bands", icon: Activity, description: "Portable band training" },
  school_gym: { name: "School/Team Gym", icon: Building, description: "Typical school weight room" }
};

const SKILL_LEVELS = [
  { id: "beginner", name: "Beginner", description: "New to training (< 6 months)", color: "text-green-400" },
  { id: "intermediate", name: "Intermediate", description: "Some experience (6 months - 2 years)", color: "text-blue-400" },
  { id: "advanced", name: "Advanced", description: "Experienced (2+ years)", color: "text-purple-400" },
  { id: "elite", name: "Elite", description: "High-level competitive athlete", color: "text-amber-400" }
];

const TrainingProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  
  // Equipment options from API
  const [allEquipment, setAllEquipment] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);
  
  // Form state
  const [equipmentPreset, setEquipmentPreset] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [skillLevel, setSkillLevel] = useState('intermediate');
  const [primaryFocusAreas, setPrimaryFocusAreas] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [limitations, setLimitations] = useState('');
  const [preferredDuration, setPreferredDuration] = useState(45);
  const [workoutsPerWeek, setWorkoutsPerWeek] = useState(4);
  
  // New injury form
  const [newInjury, setNewInjury] = useState({ area: '', description: '', severity: 'moderate' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      
      // Fetch equipment options and focus areas
      const [equipmentRes, focusRes, profileRes] = await Promise.all([
        axios.get(`${API}/api/athlete-profile/equipment-options`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/athlete-profile/focus-areas`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/athlete-profile/profile`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setAllEquipment(equipmentRes.data.all_equipment || []);
      setFocusAreas(focusRes.data.focus_areas || []);
      
      if (profileRes.data.has_profile) {
        setHasProfile(true);
        const profile = profileRes.data.profile;
        setEquipmentPreset(profile.equipment_preset || '');
        setSelectedEquipment(profile.equipment_available || []);
        setSkillLevel(profile.skill_level || 'intermediate');
        setPrimaryFocusAreas(profile.primary_focus_areas || []);
        setInjuries(profile.injuries || []);
        setLimitations((profile.limitations || []).join(', '));
        setPreferredDuration(profile.preferred_workout_duration || 45);
        setWorkoutsPerWeek(profile.workouts_per_week_target || 4);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePresetSelect = (preset) => {
    setEquipmentPreset(preset);
    // Auto-select equipment from preset
    const presetEquipment = {
      full_gym: ["barbell", "dumbbells", "squat_rack", "bench_press", "cable_machine", "leg_press", "lat_pulldown", "pull_up_bar", "resistance_bands", "foam_roller"],
      home_gym: ["dumbbells", "resistance_bands", "pull_up_bar", "bench", "kettlebells", "foam_roller", "yoga_mat"],
      bodyweight_only: ["yoga_mat", "pull_up_bar"],
      resistance_bands: ["resistance_bands", "door_anchor", "yoga_mat"],
      school_gym: ["barbell", "dumbbells", "squat_rack", "bench_press", "pull_up_bar", "resistance_bands", "medicine_balls", "agility_ladder"]
    };
    setSelectedEquipment(presetEquipment[preset] || []);
  };

  const toggleEquipment = (equipId) => {
    setSelectedEquipment(prev => 
      prev.includes(equipId) 
        ? prev.filter(e => e !== equipId)
        : [...prev, equipId]
    );
    // Clear preset if manually editing
    setEquipmentPreset('');
  };

  const toggleFocusArea = (areaId) => {
    setPrimaryFocusAreas(prev => {
      if (prev.includes(areaId)) {
        return prev.filter(a => a !== areaId);
      }
      if (prev.length >= 3) {
        toast.error('Maximum 3 focus areas');
        return prev;
      }
      return [...prev, areaId];
    });
  };

  const addInjury = () => {
    if (!newInjury.area.trim()) {
      toast.error('Enter the injury area');
      return;
    }
    setInjuries(prev => [...prev, { ...newInjury }]);
    setNewInjury({ area: '', description: '', severity: 'moderate' });
  };

  const removeInjury = (index) => {
    setInjuries(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('aura_token');
      
      await axios.post(`${API}/api/athlete-profile/profile`, {
        equipment_preset: equipmentPreset || null,
        equipment_available: selectedEquipment,
        skill_level: skillLevel,
        primary_focus_areas: primaryFocusAreas,
        injuries: injuries,
        limitations: limitations.split(',').map(l => l.trim()).filter(l => l),
        preferred_workout_duration: preferredDuration,
        workouts_per_week_target: workoutsPerWeek
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Training profile saved! Your workouts will now be fully personalized.');
      setHasProfile(true);
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-aura-primary animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
              Training Profile
            </h1>
            <p className="text-aura-text-muted mt-1">
              Customize your workouts based on what you have and need
            </p>
          </div>
          {hasProfile && (
            <Badge className="bg-green-500/20 text-green-400 border-0">
              <CheckCircle className="w-3 h-3 mr-1" /> Profile Set
            </Badge>
          )}
        </div>

        <Tabs defaultValue="equipment" className="w-full">
          <TabsList className="bg-aura-surface border border-white/10 w-full justify-start">
            <TabsTrigger value="equipment" className="data-[state=active]:bg-aura-primary">
              <Dumbbell className="w-4 h-4 mr-2" /> Equipment
            </TabsTrigger>
            <TabsTrigger value="focus" className="data-[state=active]:bg-aura-primary">
              <Target className="w-4 h-4 mr-2" /> Focus Areas
            </TabsTrigger>
            <TabsTrigger value="safety" className="data-[state=active]:bg-aura-primary">
              <Shield className="w-4 h-4 mr-2" /> Safety
            </TabsTrigger>
            <TabsTrigger value="preferences" className="data-[state=active]:bg-aura-primary">
              <Settings className="w-4 h-4 mr-2" /> Preferences
            </TabsTrigger>
          </TabsList>

          {/* Equipment Tab */}
          <TabsContent value="equipment" className="mt-4 space-y-4">
            {/* Presets */}
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Quick Presets
                </CardTitle>
                <CardDescription className="text-aura-text-muted">
                  Select a preset or customize below
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(EQUIPMENT_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => handlePresetSelect(key)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        equipmentPreset === key
                          ? 'border-aura-primary bg-aura-primary/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                    >
                      <preset.icon className={`w-6 h-6 mx-auto mb-2 ${equipmentPreset === key ? 'text-aura-primary' : 'text-aura-text-muted'}`} />
                      <p className="text-white text-sm font-medium">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Custom Equipment */}
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Available Equipment ({selectedEquipment.length} selected)
                </CardTitle>
                <CardDescription className="text-aura-text-muted">
                  Check everything you have access to
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {allEquipment.map((equip) => (
                    <div 
                      key={equip.id}
                      onClick={() => toggleEquipment(equip.id)}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedEquipment.includes(equip.id)
                          ? 'bg-aura-primary/20 border border-aura-primary/50'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <Checkbox 
                        checked={selectedEquipment.includes(equip.id)}
                        className="border-white/30 data-[state=checked]:bg-aura-primary"
                      />
                      <span className="text-sm text-white">{equip.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Focus Areas Tab */}
          <TabsContent value="focus" className="mt-4 space-y-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Skill Level
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {SKILL_LEVELS.map((level) => (
                    <button
                      key={level.id}
                      onClick={() => setSkillLevel(level.id)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        skillLevel === level.id
                          ? 'border-aura-primary bg-aura-primary/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                    >
                      <p className={`font-bold ${level.color}`}>{level.name}</p>
                      <p className="text-xs text-aura-text-muted mt-1">{level.description}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Primary Focus Areas (Max 3)
                </CardTitle>
                <CardDescription className="text-aura-text-muted">
                  What do you want to improve most? We'll prioritize these while keeping you well-rounded.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {focusAreas.map((area) => (
                    <button
                      key={area.id}
                      onClick={() => toggleFocusArea(area.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        primaryFocusAreas.includes(area.id)
                          ? 'border-aura-primary bg-aura-primary/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium text-sm">{area.name}</p>
                        {primaryFocusAreas.includes(area.id) && (
                          <CheckCircle className="w-4 h-4 text-aura-primary" />
                        )}
                      </div>
                      <p className="text-xs text-aura-text-muted mt-1">{area.description}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Safety Tab */}
          <TabsContent value="safety" className="mt-4 space-y-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Injuries & Limitations
                </CardTitle>
                <CardDescription className="text-aura-text-muted">
                  Help us keep you safe. We'll work around these in your workouts.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Injuries */}
                {injuries.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-aura-text-muted">Current Injuries</Label>
                    {injuries.map((injury, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                        <div>
                          <p className="text-white font-medium">{injury.area}</p>
                          <p className="text-xs text-aura-text-muted">{injury.description || 'No details'} - {injury.severity}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeInjury(idx)}
                          className="text-red-400 hover:text-red-300"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Injury Form */}
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
                  <p className="text-white font-medium text-sm">Add Injury</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-aura-text-muted text-xs">Body Part/Area</Label>
                      <Input
                        placeholder="e.g., Left knee, Lower back"
                        value={newInjury.area}
                        onChange={(e) => setNewInjury(prev => ({ ...prev, area: e.target.value }))}
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-aura-text-muted text-xs">Severity</Label>
                      <Select value={newInjury.severity} onValueChange={(v) => setNewInjury(prev => ({ ...prev, severity: v }))}>
                        <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          <SelectItem value="minor">Minor - Can still train</SelectItem>
                          <SelectItem value="moderate">Moderate - Need modifications</SelectItem>
                          <SelectItem value="severe">Severe - Avoid area completely</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Input
                    placeholder="Brief description (optional)"
                    value={newInjury.description}
                    onChange={(e) => setNewInjury(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-aura-bg border-white/10 text-white"
                  />
                  <Button onClick={addInjury} variant="outline" className="border-amber-500/30 text-amber-400">
                    + Add Injury
                  </Button>
                </div>

                {/* General Limitations */}
                <div>
                  <Label className="text-aura-text-muted">Movement Limitations</Label>
                  <Textarea
                    placeholder="e.g., No overhead pressing, Low impact only, No jumping"
                    value={limitations}
                    onChange={(e) => setLimitations(e.target.value)}
                    className="bg-aura-bg border-white/10 text-white mt-1"
                  />
                  <p className="text-xs text-aura-text-muted mt-1">Separate multiple limitations with commas</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="mt-4 space-y-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading uppercase tracking-wider text-sm">
                  Workout Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-aura-text-muted">Preferred Workout Duration</Label>
                    <Select value={preferredDuration.toString()} onValueChange={(v) => setPreferredDuration(parseInt(v))}>
                      <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                        <SelectItem value="75">75 minutes</SelectItem>
                        <SelectItem value="90">90 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-aura-text-muted">Workouts Per Week Goal</Label>
                    <Select value={workoutsPerWeek.toString()} onValueChange={(v) => setWorkoutsPerWeek(parseInt(v))}>
                      <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        <SelectItem value="2">2 days</SelectItem>
                        <SelectItem value="3">3 days</SelectItem>
                        <SelectItem value="4">4 days</SelectItem>
                        <SelectItem value="5">5 days</SelectItem>
                        <SelectItem value="6">6 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="fixed bottom-20 left-0 right-0 p-4 bg-gradient-to-t from-aura-bg to-transparent">
          <div className="max-w-2xl mx-auto">
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-aura-primary hover:bg-aura-primary-hover py-6"
            >
              {saving ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" /> Save Training Profile</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TrainingProfilePage;
